import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { currencySymbol, type Store, type TripSummary } from '@sp/shared'
import { itemsCount, tripName } from '../lib/format.ts'

/** Odstup karty od okraje okna. */
const EDGE_PX = 12

/**
 * Karta vedle rozpracovaneho nakupu v bocnim panelu (navrh v2): obchody v poradi
 * trasy s mestem, menou a poctem polozek, pri nakupovani postup po obchodech.
 */
export function TripPreview({
  id,
  trip,
  stores,
  anchor,
  onPointerEnter,
  onPointerLeave,
}: {
  id: string
  trip: TripSummary
  stores: readonly Store[]
  /** Obdelnik radku, vedle ktereho karta visi. */
  anchor: DOMRect
  onPointerEnter(): void
  onPointerLeave(): void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [top, setTop] = useState(anchor.top - 6)

  // U spodni hrany okna se karta posune nahoru, at je cela videt.
  useLayoutEffect(() => {
    const height = ref.current?.offsetHeight ?? 0
    setTop(Math.max(EDGE_PX, Math.min(anchor.top - 6, window.innerHeight - height - EDGE_PX)))
  }, [anchor, trip])

  const shopping = trip.status === 'shopping' && trip.doneCount > 0
  const counts = new Map(trip.storeCounts.map((c) => [c.storeId ?? '', c]))
  const route = trip.storeIds.flatMap((storeId) => stores.find((s) => s.id === storeId) ?? [])
  const loose = counts.get('')
  // „Tady jste" = prvni obchod na trase, kde jeste neni vse v kosiku.
  const here = shopping
    ? route.find((store) => {
        const c = counts.get(store.id)
        return c !== undefined && c.done < c.items
      })?.id
    : undefined
  const name = tripName(trip, stores)
  const when = name.date
  const status = shopping
    ? `${trip.doneCount} z ${trip.itemCount} v košíku`
    : trip.status === 'shopping'
      ? 'nakupuje se'
      : 'plánuje se'

  const stop = (key: string, index: number | null, name: string, sub: string | null, currency: string | null) => {
    const c = counts.get(key)
    const items = c?.items ?? 0
    const done = c?.done ?? 0
    const complete = shopping && items > 0 && done === items
    return (
      <div key={key || 'bez-obchodu'} className={`trip-preview-stop${here === key ? ' is-here' : ''}`}>
        <span className={`num-badge${complete ? ' is-done' : ''}`}>{index ?? '–'}</span>
        <span className="text-col">
          <span className="trip-preview-store">{name}</span>
          {(sub || here === key) && (
            <span className="text-col-sub">{[here === key ? 'tady jste' : null, sub].filter(Boolean).join(' · ')}</span>
          )}
        </span>
        <span className="meta muted tabular">{shopping ? `${done} z ${items}` : itemsCount(items)}</span>
        {currency && <span className="currency-pill">{currency}</span>}
        {shopping && items > 0 && (
          <span className="trip-preview-bar" aria-hidden="true">
            <span style={{ width: `${Math.round((done / items) * 100)}%` }} />
          </span>
        )}
      </div>
    )
  }

  return createPortal(
    <div
      ref={ref}
      id={id}
      role="tooltip"
      className="trip-preview"
      style={{ left: anchor.right + 14, top }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <span className="trip-preview-arrow" style={{ top: Math.max(14, anchor.top - top + 16) }} aria-hidden="true" />
      <div className="text-col">
        <span className="meta muted">{[when, status].filter(Boolean).join(' · ')}</span>
        <span className="trip-preview-title">{name.title}</span>
      </div>
      <div className="trip-preview-stops">
        {route.length === 0 && <span className="meta muted">Kdekoli · {itemsCount(trip.itemCount)}</span>}
        {route.map((store, index) => stop(store.id, index + 1, store.name, store.city, currencySymbol(store.currency)))}
        {route.length > 0 && loose !== undefined && loose.items > 0 && stop('', null, 'Bez obchodu', null, null)}
      </div>
      <div className="trip-preview-foot">
        <span>{itemsCount(trip.itemCount)}</span>
        <span>Klikni pro otevření</span>
      </div>
    </div>,
    document.body,
  )
}
